import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

// Use relative URLs - works on both preview and production since /api is routed by ingress
const API = "";

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/auth/me`, {
                withCredentials: true
            });
            setUser(data);
        } catch (e) {
            // Try to refresh token
            try {
                await axios.post(`${API}/api/auth/refresh`, {}, {
                    withCredentials: true
                });
                const { data } = await axios.get(`${API}/api/auth/me`, {
                    withCredentials: true
                });
                setUser(data);
            } catch {
                setUser(false);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await axios.post(
            `${API}/api/auth/login`,
            { email, password },
            { withCredentials: true }
        );
        setUser(data);
        return data;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/api/auth/logout`, {}, {
                withCredentials: true
            });
        } finally {
            setUser(false);
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
